import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import type { PayloadAction } from '@reduxjs/toolkit';
import axios from 'axios';
import type { User } from './authSlice';
import type { ApiError } from '@/lib/api';

interface TemporaryAuthState {
  loading: boolean;
  error: string | null;
  isAuthenticated: boolean;
  user: User | null;
  token: string | null;
}

const initialState: TemporaryAuthState = {
  loading: false,
  error: null,
  isAuthenticated: false,
  user: null,
  token: null,
};

export const temporaryLogin = createAsyncThunk(
  'temporaryAuth/login',
  async (token: string, { rejectWithValue }) => {
    try {
      const response = await axios.post(
        `${process.env.NEXT_PUBLIC_API_URL}/auth/temporary-login`,
        { token }
      );

      // Store token in localStorage
      localStorage.setItem('temporaryToken', response.data.accessToken);

      return response.data;
    } catch (error: unknown) {
      const err = error as ApiError;
      return rejectWithValue(err.response?.data?.message || 'Login failed');
    }
  }
);

export const temporaryLogout = createAsyncThunk(
  'temporaryAuth/logout',
  async () => {
    localStorage.removeItem('temporaryToken');
    return null;
  }
);

const temporaryAuthSlice = createSlice({
  name: 'temporaryAuth',
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
    setAuthenticated: (state, action: PayloadAction<boolean>) => {
      state.isAuthenticated = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(temporaryLogin.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(temporaryLogin.fulfilled, (state, action) => {
        state.loading = false;
        state.isAuthenticated = true;
        state.user = action.payload;
        state.token = action.payload.accessToken;
        state.error = null;
      })
      .addCase(temporaryLogin.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
        state.isAuthenticated = false;
        state.user = null;
        state.token = null;
      })
      .addCase(temporaryLogout.fulfilled, (state) => {
        state.isAuthenticated = false;
        state.user = null;
        state.token = null;
        state.error = null;
      });
  },
});

export const { clearError, setAuthenticated } = temporaryAuthSlice.actions;
export default temporaryAuthSlice.reducer;
